import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Trash2, Plus, Image, Download, ExternalLink, Pencil, X, Eye, Sparkles, ChevronRight, Loader2, CheckCircle, AlertCircle, RefreshCw, Check, Heart, Grid, Layers, Share2, Link, Copy, Search, Link2, Unlink } from 'lucide-react';
import { VizColorLinker } from './visualizations/VizColorLinker';
import { MissingRoomsPanel } from './visualizations/MissingRoomsPanel';
import { VizGallery, vizThumb } from './visualizations/VizGallery';
import { SharePreviewDialog, SharedLinksDialog } from './visualizations/SharePreviewDialog';
import { EmailComposer } from './email/EmailComposer';
import { useAuth } from '../../context/AuthContext';
import { tierLabels } from '../../lib/fabricLabels';
import { Button } from '../ui/button';
import { RegenCollectionDialog } from './RegenCollectionDialog';
import { RomanRegenDialog } from './RomanRegenDialog';
import { SceneReviewDialog } from './SceneReviewDialog';
import { DefaultRoomDialog, RestoreBinDialog } from './CollectionRoomTools';
import { RoomCoverageDialog, RoomInsightsDialog } from './RoomCoverageTools';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { useCompanyBrand } from '../../hooks/useCompanyBrand';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Image component with loading state, auto-retry, and error handling
const ImageWithLoading = ({ src, alt, className, onClick, colorHex }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const retryRef = useRef(0);
  const MAX_RETRIES = 2;
  const [imgSrc, setImgSrc] = useState(src);

  useEffect(() => { retryRef.current = 0; setError(false); setLoading(true); setImgSrc(src); }, [src]);

  const handleError = () => {
    if (retryRef.current < MAX_RETRIES) {
      retryRef.current += 1;
      const delay = retryRef.current * 1500;
      setTimeout(() => setImgSrc(`${src}${src.includes('?') ? '&' : '?'}retry=${retryRef.current}`), delay);
    } else {
      setLoading(false);
      setError(true);
    }
  };

  const handleRetryClick = () => {
    retryRef.current = 0;
    setError(false);
    setLoading(true);
    setImgSrc(`${src}${src.includes('?') ? '&' : '?'}retry=manual-${Date.now()}`);
  };
  
  return (
    <div className="relative w-full h-full">
      {loading && !error && (
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{ backgroundColor: colorHex || '#f5f5f5' }}
        >
          <div className="flex flex-col items-center gap-2 text-white/80">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="text-xs">Loading...</span>
          </div>
        </div>
      )}
      
      {error && (
        <div 
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          style={{ backgroundColor: colorHex || '#f5f5f5' }}
          onClick={handleRetryClick}
          title="Click to retry"
        >
          <div className="flex flex-col items-center gap-2 text-neutral-500">
            <AlertCircle className="w-8 h-8" />
            <span className="text-xs text-center px-2">Tap to retry</span>
          </div>
        </div>
      )}
      
      <img 
        src={imgSrc}
        alt={alt}
        className={`${className} ${loading || error ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        onClick={onClick}
        onLoad={() => setLoading(false)}
        onError={handleError}
      />
    </div>
  );
};

export const VisualizationsTab = ({ token }) => {
  const brand = useCompanyBrand();
  const { user } = useAuth();
  const [shareOpen, setShareOpen] = useState(false);
  const [sharedLinksOpen, setSharedLinksOpen] = useState(false);
  const [shareComposer, setShareComposer] = useState(null);
  const [visualizations, setVisualizations] = useState([]);
  const [fabricCollections, setFabricCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCollections, setLoadingCollections] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [showBulkGenerateDialog, setShowBulkGenerateDialog] = useState(false);
  const [showBatchRoomDialog, setShowBatchRoomDialog] = useState(false);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [selectedViz, setSelectedViz] = useState(null);
  const [selectedColorForAI, setSelectedColorForAI] = useState(null);
  const [productFilter, setProductFilter] = useState('roller-shades');
  const [brandFilter, setBrandFilter] = useState('');
  const [collectionFilter, setCollectionFilter] = useState('');
  const [materialFilter, setMaterialFilter] = useState('');
  const [vizSearch, setVizSearch] = useState('');
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkLink, setBulkLink] = useState(null);
  const [showBulkLinkDialog, setShowBulkLinkDialog] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [relinking, setRelinking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'missing'
  const [regenerating, setRegenerating] = useState(false);
  const [compareSelection, setCompareSelection] = useState([]); // For side-by-side comparison
  const [favoriteRoomStyles, setFavoriteRoomStyles] = useState(() => {
    // Load favorites from localStorage
    const saved = localStorage.getItem('apollo_favorite_room_styles');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Admin-defined Style Presets (stored in DB later, for now localStorage)
  const [stylePresets, setStylePresets] = useState(() => {
    const saved = localStorage.getItem('apollo_style_presets');
    return saved ? JSON.parse(saved) : [
      { id: 'preset-1', name: 'Modern Living', room_type: 'living room', style: 'modern minimalist', interior_color_scheme: 'neutral', description: 'Clean lines, natural light' },
      { id: 'preset-2', name: 'Cozy Bedroom', room_type: 'bedroom', style: 'scandinavian', interior_color_scheme: 'warm', description: 'Soft textures, warm tones' },
      { id: 'preset-3', name: 'Professional Office', room_type: 'home office', style: 'contemporary', interior_color_scheme: 'cool', description: 'Focused, calm workspace' }
    ];
  });
  const [showPresetsDialog, setShowPresetsDialog] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  
  // AI Regeneration form for preview dialog
  const [regenForm, setRegenForm] = useState({
    room_type: 'living room',
    style: 'modern minimalist',
    interior_color_scheme: 'neutral',
    count: 3,
    description: ''
  });
  
  // Batch Room Styles form
  const [batchRoomForm, setBatchRoomForm] = useState({
    room_types: ['living room', 'bedroom', 'home office'],
    style: 'modern minimalist',
    interior_color_scheme: 'neutral',
    description: ''
  });
  
  // Hierarchical selection for upload
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  
  const [uploadForm, setUploadForm] = useState({
    product_type: 'roller-shades',
    brand_id: '',
    brand_name: '',
    collection_id: '',
    collection_name: '',
    material_id: '',
    material_name: '',
    color_id: '',
    color_name: '',
    color_hex: '',
    title: '',
    file: null
  });
  
  const [editForm, setEditForm] = useState({
    id: '',
    title: '',
    product_type: '',
    fabric_material: '',
    fabric_color: ''
  });
  const [editLink, setEditLink] = useState(null);
  
  const [aiForm, setAiForm] = useState({
    room_type: 'living room',
    style: 'modern minimalist',
    description: '',
    count: 1
  });

  const productTypes = [
    { id: 'roller-shades', name: 'Roller Shades' },
    { id: 'solar-shades', name: 'Solar Shades' },
    { id: 'motorized-drapes', name: 'Motorized Drapes' },
    { id: 'roman-shades', name: 'Roman Shades' }
  ];

  const roomTypes = [
    'living room', 'bedroom', 'dining room', 'home office', 
    'kitchen', 'bathroom', 'nursery', 'sunroom'
  ];

  const styleOptions = [
    'modern minimalist', 'contemporary', 'traditional', 'scandinavian',
    'industrial', 'coastal', 'farmhouse', 'mid-century modern', 'bohemian'
  ];

  // Fetch fabric collections
  useEffect(() => {
    const fetchCollections = async () => {
      try {
        setLoadingCollections(true);
        const response = await axios.get(
          `${API_URL}/fabric-collections/visible?product_type=${productFilter}`
        );
        setFabricCollections(response.data || []);
      } catch (error) {
        console.error('Failed to fetch fabric collections:', error);
        toast.error('Failed to load fabric collections');
      } finally {
        setLoadingCollections(false);
      }
    };
    fetchCollections();
  }, [productFilter]);

  // Fetch visualizations
  useEffect(() => {
    fetchVisualizations();
  }, [productFilter, token]);

  const fetchVisualizations = async () => {
    try {
      setLoading(true);
      const url = `${API_URL}/admin/visualizations?product_type=${productFilter}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVisualizations(response.data);
    } catch (error) {
      console.error('Failed to fetch visualizations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get all colors from fabric collections as flat list with hierarchy info
  const allColors = useMemo(() => {
    const colors = [];
    fabricCollections.forEach(brand => {
      // Brand-level fabric properties
      const brandFabricType = brand.fabric_type;
      const brandOpenness = brand.openness;
      
      // Check if brand has direct colors (2-tier)
      if (brand.colors?.length > 0) {
        brand.colors.forEach(color => {
          colors.push({
            ...color,
            brand_id: brand.id || brand.brand_id,
            brand_name: brand.name,
            collection_id: null,
            collection_name: null,
            material_id: null,
            material_name: null,
            fabric_type: brandFabricType,
            openness: brandOpenness,
            path: `${brand.name} → ${color.name}`
          });
        });
      }
      
      brand.collections?.forEach(collection => {
        if (collection.materials?.length > 0) {
          // 4-tier: Brand > Collection > Material > Color
          collection.materials.forEach(material => {
            material.colors?.forEach(color => {
              colors.push({
                ...color,
                brand_id: brand.id || brand.brand_id,
                brand_name: brand.name,
                collection_id: collection.id,
                collection_name: collection.name,
                material_id: material.id,
                material_name: material.name,
                fabric_type: brandFabricType,
                openness: brandOpenness,
                path: `${brand.name} → ${collection.name} → ${material.name} → ${color.name}`
              });
            });
          });
        } else if (collection.colors?.length > 0) {
          // 3-tier: Brand > Collection > Color
          collection.colors.forEach(color => {
            colors.push({
              ...color,
              brand_id: brand.id || brand.brand_id,
              brand_name: brand.name,
              collection_id: collection.id,
              collection_name: collection.name,
              material_id: null,
              material_name: null,
              fabric_type: brandFabricType,
              openness: brandOpenness,
              path: `${brand.name} → ${collection.name} → ${color.name}`
            });
          });
        }
      });
    });
    return colors;
  }, [fabricCollections]);

  // Filter colors by brand, collection, and material/type
  const filteredColors = useMemo(() => {
    let filtered = allColors;
    if (brandFilter) filtered = filtered.filter(c => c.brand_id === brandFilter);
    if (collectionFilter) filtered = filtered.filter(c => c.collection_id === collectionFilter);
    if (materialFilter) filtered = filtered.filter(c => c.material_id === materialFilter);
    return filtered;
  }, [allColors, brandFilter, collectionFilter, materialFilter]);

  // Derive unique collections and materials for filter dropdowns
  const filterCollections = useMemo(() => {
    const colls = new Map();
    (brandFilter ? allColors.filter(c => c.brand_id === brandFilter) : allColors).forEach(c => {
      if (c.collection_id && c.collection_name) colls.set(c.collection_id, c.collection_name);
    });
    return Array.from(colls, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allColors, brandFilter]);

  const filterMaterials = useMemo(() => {
    const mats = new Map();
    let base = allColors;
    if (brandFilter) base = base.filter(c => c.brand_id === brandFilter);
    if (collectionFilter) base = base.filter(c => c.collection_id === collectionFilter);
    base.forEach(c => {
      if (c.material_id && c.material_name) mats.set(c.material_id, c.material_name);
    });
    return Array.from(mats, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allColors, brandFilter, collectionFilter]);

  // Get colors without visualizations
  const colorsWithoutViz = useMemo(() => {
    const vizColorIds = new Set(visualizations.map(v => v.color_id).filter(Boolean));
    return filteredColors.filter(color => !vizColorIds.has(color.id));
  }, [filteredColors, visualizations]);

  // Resolve each visualization against the live catalog (by current color id) and expose article code + link state
  const colorById = useMemo(() => new Map(allColors.map(c => [c.id, c])), [allColors]);
  const resolvedVisualizations = useMemo(() => visualizations.map(v => {
    const c = colorById.get(v.color_id);
    return {
      ...v,
      linked: !!c,
      color_code: v.color_code || c?.code || '',
      brand_id: c?.brand_id || v.brand_id, collection_id: c?.collection_id || v.collection_id, material_id: c?.material_id || v.material_id,
      brand_name: c?.brand_name || v.brand_name, collection_name: c?.collection_name || v.collection_name, material_name: c?.material_name || v.material_name || v.fabric_material,
      color_name: c?.name || v.color_name || v.fabric_color,
    };
  }), [visualizations, colorById]);
  const unlinkedCount = useMemo(() => resolvedVisualizations.filter(v => !v.linked).length, [resolvedVisualizations]);

  // Filter by brand/collection/material metadata + free-text search
  const filteredVisualizations = useMemo(() => {
    const q = vizSearch.trim().toLowerCase();
    return resolvedVisualizations.filter(v =>
      (!brandFilter || v.brand_id === brandFilter) &&
      (!collectionFilter || v.collection_id === collectionFilter) &&
      (!materialFilter || v.material_id === materialFilter) &&
      (!q || [v.title, v.color_name, v.color_code, v.material_name, v.collection_name, v.brand_name, v.room_type, v.style].some(x => (x || '').toLowerCase().includes(q)))
    );
  }, [resolvedVisualizations, brandFilter, collectionFilter, materialFilter, vizSearch]);

  const unlinkedIds = useMemo(() => resolvedVisualizations.filter(v => !v.linked).map(v => v.id), [resolvedVisualizations]);
  const toggleBulk = (id) => setBulkSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const handleBulkDelete = async (all = false) => {
    const count = all ? unlinkedIds.length : bulkSelected.size;
    if (!count || !window.confirm(`Delete ${count} unlinked visualization(s)? This cannot be undone.`)) return;
    try {
      setBulkBusy(true);
      const { data } = await axios.post(`${API_URL}/admin/visualizations/bulk-delete`, all ? { unlinked: true } : { ids: [...bulkSelected] }, bulkHeaders);
      toast.success(`Deleted ${data.deleted} visualizations`);
      setBulkSelected(new Set());
      fetchVisualizations();
    } catch { toast.error('Bulk delete failed'); } finally { setBulkBusy(false); }
  };

  const handleBulkLink = async () => {
    if (!bulkLink?.color_id || !bulkSelected.size) return;
    try {
      setBulkBusy(true);
      const { data } = await axios.post(`${API_URL}/admin/visualizations/bulk-link`, { ids: [...bulkSelected], color_id: bulkLink.color_id }, bulkHeaders);
      toast.success(`Linked ${data.linked} visualizations to ${bulkLink.color_name}`);
      setShowBulkLinkDialog(false); setBulkLink(null); setBulkSelected(new Set());
      fetchVisualizations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk link failed'); } finally { setBulkBusy(false); }
  };

  const handleRelink = async () => {
    try {
      setRelinking(true);
      const { data } = await axios.post(`${API_URL}/admin/visualizations/relink`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Re-linked ${data.relinked} visualizations · ${data.already_linked} already linked · ${data.unmatched} need manual linking`);
      fetchVisualizations();
    } catch {
      toast.error('Re-link failed');
    } finally {
      setRelinking(false);
    }
  };

  // Get available collections for selected brand (upload dialog)
  const availableCollections = useMemo(() => {
    if (!selectedBrand) return [];
    const brand = fabricCollections.find(b => b.id === selectedBrand.id);
    return brand?.collections || [];
  }, [selectedBrand, fabricCollections]);

  // Get available materials for selected collection
  const availableMaterials = useMemo(() => {
    if (!selectedCollection) return [];
    return selectedCollection.materials || [];
  }, [selectedCollection]);

  // Get available colors for selected material or collection
  const availableColors = useMemo(() => {
    if (selectedMaterial) {
      return selectedMaterial.colors || [];
    }
    if (selectedCollection && (!selectedCollection.materials || selectedCollection.materials.length === 0)) {
      return selectedCollection.colors || [];
    }
    return [];
  }, [selectedMaterial, selectedCollection]);

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    setSelectedCollection(null);
    setSelectedMaterial(null);
    setSelectedColor(null);
    setUploadForm(prev => ({
      ...prev,
      brand_id: brand.id,
      brand_name: brand.name,
      collection_id: '',
      collection_name: '',
      material_id: '',
      material_name: '',
      color_id: '',
      color_name: '',
      color_hex: ''
    }));
  };

  const handleCollectionSelect = (collection) => {
    setSelectedCollection(collection);
    setSelectedMaterial(null);
    setSelectedColor(null);
    setUploadForm(prev => ({
      ...prev,
      collection_id: collection.id,
      collection_name: collection.name,
      material_id: '',
      material_name: '',
      color_id: '',
      color_name: '',
      color_hex: ''
    }));
  };

  const handleMaterialSelect = (material) => {
    setSelectedMaterial(material);
    setSelectedColor(null);
    setUploadForm(prev => ({
      ...prev,
      material_id: material.id,
      material_name: material.name,
      color_id: '',
      color_name: '',
      color_hex: ''
    }));
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    const title = selectedMaterial 
      ? `${selectedBrand?.name} - ${selectedCollection?.name} - ${selectedMaterial?.name} - ${color.name}`
      : `${selectedBrand?.name} - ${selectedCollection?.name} - ${color.name}`;
    setUploadForm(prev => ({
      ...prev,
      color_id: color.id,
      color_name: color.name,
      color_hex: color.hex_color || '',
      title: title
    }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Please select an image');
      return;
    }
    if (!uploadForm.color_id) {
      toast.error('Please select a color from the hierarchy');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadForm.file);
    formData.append('product_type', uploadForm.product_type);
    formData.append('brand_id', uploadForm.brand_id);
    formData.append('brand_name', uploadForm.brand_name);
    formData.append('collection_id', uploadForm.collection_id);
    formData.append('collection_name', uploadForm.collection_name);
    formData.append('material_id', uploadForm.material_id || '');
    formData.append('material_name', uploadForm.material_name || '');
    formData.append('color_id', uploadForm.color_id);
    formData.append('color_name', uploadForm.color_name);
    formData.append('color_hex', uploadForm.color_hex);
    formData.append('title', uploadForm.title || `${uploadForm.brand_name} - ${uploadForm.color_name}`);
    // Keep backward compatibility
    formData.append('fabric_material', uploadForm.material_name || uploadForm.collection_name);
    formData.append('fabric_color', uploadForm.color_name);

    try {
      await axios.post(`${API_URL}/admin/visualizations/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Visualization uploaded successfully');
      setShowUploadDialog(false);
      resetUploadForm();
      fetchVisualizations();
    } catch (error) {
      toast.error('Failed to upload visualization');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setSelectedBrand(null);
    setSelectedCollection(null);
    setSelectedMaterial(null);
    setSelectedColor(null);
    setUploadForm({
      product_type: productFilter,
      brand_id: '',
      brand_name: '',
      collection_id: '',
      collection_name: '',
      material_id: '',
      material_name: '',
      color_id: '',
      color_name: '',
      color_hex: '',
      title: '',
      file: null
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this visualization?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/visualizations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Visualization deleted');
      fetchVisualizations();
    } catch (error) {
      toast.error('Failed to delete visualization');
    }
  };

  const handleEdit = (viz) => {
    setEditForm({
      id: viz.id,
      title: viz.title || '',
      product_type: viz.product_type || 'roller-shades',
      fabric_material: viz.fabric_material || '',
      fabric_color: viz.fabric_color || ''
    });
    const c = colorById.get(viz.color_id);
    setEditLink(c ? { brand_id: c.brand_id, brand_name: c.brand_name, collection_id: c.collection_id, collection_name: c.collection_name, material_id: c.material_id, material_name: c.material_name, color_id: c.id, color_name: c.name, color_code: c.code, color_hex: c.hex_color } : null);
    setSelectedViz(viz);
    setShowEditDialog(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/admin/visualizations/${editForm.id}`, {
        title: editForm.title,
        product_type: editForm.product_type,
        fabric_material: editLink?.material_name || editLink?.collection_name || editForm.fabric_material,
        fabric_color: editLink?.color_name || editForm.fabric_color,
        ...(editLink || {})
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Visualization updated');
      setShowEditDialog(false);
      fetchVisualizations();
    } catch (error) {
      toast.error('Failed to update visualization');
    }
  };

  const handleDownload = async (viz) => {
    try {
      const imageUrl = viz.external_url || `${process.env.REACT_APP_BACKEND_URL}${viz.image_url}`;
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const filename = viz.image_url?.split('/').pop() || `${viz.title.replace(/\s+/g, '_')}.png`;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image');
    }
  };

  const handleOpenOriginal = (viz) => {
    const imageUrl = viz.external_url || `${process.env.REACT_APP_BACKEND_URL}${viz.image_url}`;
    window.open(imageUrl, '_blank');
  };

  const handlePreview = (viz) => {
    setSelectedViz(viz);
    // Reset regeneration form when opening preview
    setRegenForm({
      room_type: 'living room',
      style: 'modern minimalist',
      interior_color_scheme: 'neutral',
      count: 3,
      description: ''
    });
    setShowPreviewDialog(true);
  };

  // Handle regenerating AI variations from preview dialog
  const handleRegenerateVariations = async () => {
    if (!selectedViz) return;
    
    setRegenerating(true);
    try {
      let successCount = 0;
      let failCount = 0;
      
      // Find the color info from allColors
      const colorInfo = allColors.find(c => 
        c.id === selectedViz.color_id || 
        c.name === selectedViz.fabric_color ||
        c.name === selectedViz.color_name
      );
      
      // Build color scheme description for prompt
      const colorSchemeDesc = regenForm.interior_color_scheme !== 'neutral' 
        ? ` The room should have a ${regenForm.interior_color_scheme} color scheme.` 
        : '';
      
      for (let i = 0; i < regenForm.count; i++) {
        try {
          await axios.post(
            `${API_URL}/admin/visualizations/generate-ai`,
            {
              product_type: selectedViz.product_type || productFilter,
              brand_id: selectedViz.brand_id || colorInfo?.brand_id,
              brand_name: selectedViz.brand_name || colorInfo?.brand_name,
              collection_id: selectedViz.collection_id || colorInfo?.collection_id,
              collection_name: selectedViz.collection_name || colorInfo?.collection_name,
              material_id: selectedViz.material_id || colorInfo?.material_id,
              material_name: selectedViz.material_name || selectedViz.fabric_material || colorInfo?.material_name,
              color_id: selectedViz.color_id || colorInfo?.id,
              color_name: selectedViz.color_name || selectedViz.fabric_color || colorInfo?.name,
              color_hex: selectedViz.color_hex || colorInfo?.hex_color,
              room_type: regenForm.room_type,
              style: regenForm.style,
              description: `${regenForm.description}${colorSchemeDesc}`,
              count: 1,
              fabric_type: colorInfo?.fabric_type,
              openness: colorInfo?.openness
            },
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          successCount++;
        } catch (error) {
          console.error(`Failed to generate variation ${i + 1}:`, error);
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Generated ${successCount} new variation(s)`);
        fetchVisualizations();
      }
      if (failCount > 0) {
        toast.error(`Failed to generate ${failCount} variation(s)`);
      }
    } catch (error) {
      console.error('Failed to regenerate variations:', error);
      toast.error('Failed to generate variations');
    } finally {
      setRegenerating(false);
    }
  };

  const openAIDialog = (color) => {
    setSelectedColorForAI(color);
    setAiForm({
      room_type: 'living room',
      style: 'modern minimalist',
      description: '',
      count: 1
    });
    setShowAIDialog(true);
  };

  const handleGenerateAI = async () => {
    if (!selectedColorForAI) return;
    
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/visualizations/generate-ai`,
        {
          product_type: productFilter,
          brand_id: selectedColorForAI.brand_id,
          brand_name: selectedColorForAI.brand_name,
          collection_id: selectedColorForAI.collection_id,
          collection_name: selectedColorForAI.collection_name,
          material_id: selectedColorForAI.material_id,
          material_name: selectedColorForAI.material_name,
          color_id: selectedColorForAI.id,
          color_name: selectedColorForAI.name,
          color_hex: selectedColorForAI.hex_color,
          room_type: aiForm.room_type,
          style: aiForm.style,
          description: aiForm.description,
          count: aiForm.count,
          // Pass fabric properties for opacity-aware generation
          fabric_type: selectedColorForAI.fabric_type,
          openness: selectedColorForAI.openness
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`Generated ${response.data.generated?.length || 1} visualization(s) successfully`);
      setShowAIDialog(false);
      fetchVisualizations();
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate AI visualization');
    } finally {
      setGenerating(false);
    }
  };

  const handleBulkGenerate = async () => {
    if (colorsWithoutViz.length === 0) {
      toast.info('All colors already have visualizations');
      return;
    }
    
    setGenerating(true);
    let successCount = 0;
    let failCount = 0;
    
    // Generate for first 5 colors (to avoid timeout)
    const colorsToGenerate = colorsWithoutViz.slice(0, 5);
    
    for (const color of colorsToGenerate) {
      try {
        await axios.post(
          `${API_URL}/admin/visualizations/generate-ai`,
          {
            product_type: productFilter,
            brand_id: color.brand_id,
            brand_name: color.brand_name,
            collection_id: color.collection_id,
            collection_name: color.collection_name,
            material_id: color.material_id,
            material_name: color.material_name,
            color_id: color.id,
            color_name: color.name,
            color_hex: color.hex_color,
            room_type: 'living room',
            style: 'modern minimalist',
            count: 1,
            // Pass fabric properties for opacity-aware generation
            fabric_type: color.fabric_type,
            openness: color.openness
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        successCount++;
      } catch (error) {
        console.error(`Failed to generate for ${color.name}:`, error);
        failCount++;
      }
    }
    
    setGenerating(false);
    setShowBulkGenerateDialog(false);
    fetchVisualizations();
    
    if (successCount > 0) {
      toast.success(`Generated ${successCount} visualization(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to generate ${failCount} visualization(s)`);
    }
  };
  
  // Handle Batch Room Styles - Generate one color across multiple room types
  const handleBatchRoomGenerate = async () => {
    if (!selectedColorForAI || batchRoomForm.room_types.length === 0) return;
    
    setGenerating(true);
    let successCount = 0;
    let failCount = 0;
    
    const colorSchemeDesc = batchRoomForm.interior_color_scheme !== 'neutral' 
      ? ` The room should have a ${batchRoomForm.interior_color_scheme} color scheme.` 
      : '';
    
    for (const roomType of batchRoomForm.room_types) {
      try {
        await axios.post(
          `${API_URL}/admin/visualizations/generate-ai`,
          {
            product_type: productFilter,
            brand_id: selectedColorForAI.brand_id,
            brand_name: selectedColorForAI.brand_name,
            collection_id: selectedColorForAI.collection_id,
            collection_name: selectedColorForAI.collection_name,
            material_id: selectedColorForAI.material_id,
            material_name: selectedColorForAI.material_name,
            color_id: selectedColorForAI.id,
            color_name: selectedColorForAI.name,
            color_hex: selectedColorForAI.hex_color,
            room_type: roomType,
            style: batchRoomForm.style,
            description: `${batchRoomForm.description}${colorSchemeDesc}`,
            count: 1,
            fabric_type: selectedColorForAI.fabric_type,
            openness: selectedColorForAI.openness
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        successCount++;
      } catch (error) {
        console.error(`Failed to generate for ${roomType}:`, error);
        failCount++;
      }
    }
    
    setGenerating(false);
    setShowBatchRoomDialog(false);
    fetchVisualizations();
    
    if (successCount > 0) {
      toast.success(`Generated ${successCount} room visualization(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to generate ${failCount} room(s)`);
    }
  };
  
  // Toggle room type selection for batch
  const toggleBatchRoomType = (roomType) => {
    setBatchRoomForm(prev => ({
      ...prev,
      room_types: prev.room_types.includes(roomType)
        ? prev.room_types.filter(r => r !== roomType)
        : [...prev.room_types, roomType]
    }));
  };
  
  // Toggle visualization for comparison
  const toggleCompareSelection = (viz) => {
    setCompareSelection(prev => {
      const isSelected = prev.some(v => v.id === viz.id);
      if (isSelected) {
        return prev.filter(v => v.id !== viz.id);
      }
      if (prev.length >= 4) {
        toast.info('Maximum 4 visualizations for comparison');
        return prev;
      }
      return [...prev, viz];
    });
  };
  
  // Save favorite room style
  const saveFavoriteRoomStyle = (styleConfig) => {
    const newFavorites = [...favoriteRoomStyles, { 
      id: Date.now(), 
      ...styleConfig,
      created_at: new Date().toISOString()
    }];
    setFavoriteRoomStyles(newFavorites);
    localStorage.setItem('apollo_favorite_room_styles', JSON.stringify(newFavorites));
    toast.success('Room style saved to favorites');
  };
  
  // Remove favorite room style
  const removeFavoriteRoomStyle = (id) => {
    const newFavorites = favoriteRoomStyles.filter(f => f.id !== id);
    setFavoriteRoomStyles(newFavorites);
    localStorage.setItem('apollo_favorite_room_styles', JSON.stringify(newFavorites));
    toast.success('Removed from favorites');
  };
  
  // Apply favorite room style to form
  const applyFavoriteStyle = (favorite) => {
    setRegenForm({
      room_type: favorite.room_type,
      style: favorite.style,
      interior_color_scheme: favorite.interior_color_scheme,
      count: 1,
      description: favorite.description || ''
    });
    toast.success(`Applied "${favorite.style}" style`);
  };
  
  // Style Presets Management
  const saveStylePreset = (preset) => {
    const newPresets = [...stylePresets, { 
      id: `preset-${Date.now()}`, 
      ...preset,
      created_at: new Date().toISOString()
    }];
    setStylePresets(newPresets);
    localStorage.setItem('apollo_style_presets', JSON.stringify(newPresets));
    toast.success('Style preset saved');
  };
  
  const updateStylePreset = (id, updates) => {
    const newPresets = stylePresets.map(p => p.id === id ? { ...p, ...updates } : p);
    setStylePresets(newPresets);
    localStorage.setItem('apollo_style_presets', JSON.stringify(newPresets));
    toast.success('Style preset updated');
  };
  
  const deleteStylePreset = (id) => {
    const newPresets = stylePresets.filter(p => p.id !== id);
    setStylePresets(newPresets);
    localStorage.setItem('apollo_style_presets', JSON.stringify(newPresets));
    toast.success('Style preset deleted');
  };
  
  const applyStylePreset = (preset) => {
    setRegenForm({
      room_type: preset.room_type,
      style: preset.style,
      interior_color_scheme: preset.interior_color_scheme,
      count: 1,
      description: preset.description || ''
    });
    toast.success(`Applied "${preset.name}" preset`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-semibold">Product Visualizations</h3>
          <p className="text-sm text-neutral-500">
            Manage visualization images for each fabric color from your collections
          </p>
        </div>
        <div className="flex gap-2">
          <RegenCollectionDialog allColors={allColors} token={token} />
          <RomanRegenDialog token={token} />
          <SceneReviewDialog token={token} />
          <DefaultRoomDialog allColors={allColors} token={token} />
          <RestoreBinDialog token={token} />
          <RoomCoverageDialog allColors={allColors} token={token} />
          <RoomInsightsDialog token={token} />
          <Dialog open={showBulkGenerateDialog} onOpenChange={setShowBulkGenerateDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> 
                Bulk Generate AI
                {colorsWithoutViz.length > 0 && (
                  <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full ml-1">
                    {colorsWithoutViz.length} missing
                  </span>
                )}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bulk Generate AI Visualizations</DialogTitle>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    <strong>{colorsWithoutViz.length}</strong> colors are missing visualizations.
                    This will generate images for the first 5 colors without visualizations.
                  </p>
                </div>
                <div className="text-sm text-neutral-600">
                  Colors to generate:
                  <ul className="mt-2 space-y-1">
                    {colorsWithoutViz.slice(0, 5).map(color => (
                      <li key={color.id} className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded border"
                          style={{ backgroundColor: color.hex_color || '#ccc' }}
                        />
                        <span>{color.path}</span>
                      </li>
                    ))}
                    {colorsWithoutViz.length > 5 && (
                      <li className="text-neutral-400">...and {colorsWithoutViz.length - 5} more</li>
                    )}
                  </ul>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowBulkGenerateDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleBulkGenerate} 
                  disabled={generating || colorsWithoutViz.length === 0}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {generating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Generate {Math.min(5, colorsWithoutViz.length)} Images</>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {compareSelection.length > 0 && (
            <Button onClick={() => setShareOpen(true)} className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700" data-testid="viz-share-btn">
              <Share2 className="w-4 h-4" /> Share with customer ({compareSelection.length})
            </Button>
          )}
          <Button variant="outline" onClick={() => setSharedLinksOpen(true)} className="flex items-center gap-2" data-testid="viz-shared-links-btn">
            <Link className="w-4 h-4" /> Shared links
          </Button>
          {/* Compare Mode Toggle */}
          {compareSelection.length > 0 && (
            <Button 
              variant="outline" 
              onClick={() => setShowCompareDialog(true)}
              className="flex items-center gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
            >
              <CheckCircle className="w-4 h-4" />
              Compare ({compareSelection.length})
            </Button>
          )}
          
          {/* Style Presets Button */}
          <Button 
            variant="outline" 
            onClick={() => setShowPresetsDialog(true)}
            className="flex items-center gap-2"
          >
            <Grid className="w-4 h-4" />
            Style Presets
          </Button>
          
          <Dialog open={showUploadDialog} onOpenChange={(open) => {
            setShowUploadDialog(open);
            if (!open) resetUploadForm();
          }}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="w-4 h-4" /> Upload Visualization
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Upload Visualization Image</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4">
                {/* Product Type */}
                <div>
                  <Label>Product Type</Label>
                  <select
                    value={uploadForm.product_type}
                    onChange={(e) => setUploadForm({ ...uploadForm, product_type: e.target.value })}
                    className="w-full border rounded-md p-2 mt-1"
                  >
                    {productTypes.map(pt => (
                      <option key={pt.id} value={pt.id}>{pt.name}</option>
                    ))}
                  </select>
                </div>

                {/* Hierarchical Selection */}
                <div className="space-y-4 p-4 bg-neutral-50 rounded-lg">
                  <Label className="text-base font-semibold">Select Color from Collection</Label>
                  
                  {/* Step 1: Brand */}
                  <div>
                    <Label className="text-sm text-neutral-600">1. Select Brand</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {fabricCollections.map(brand => (
                        <button
                          key={brand.id}
                          type="button"
                          onClick={() => handleBrandSelect(brand)}
                          className={`px-3 py-2 rounded-md text-sm transition-colors ${
                            selectedBrand?.id === brand.id
                              ? 'bg-amber-600 text-white'
                              : 'bg-white border hover:bg-neutral-100'
                          }`}
                        >
                          {brand.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Step 2: Collection */}
                  {selectedBrand && (
                    <div>
                      <Label className="text-sm text-neutral-600">2. Select Collection</Label>
                      <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto">
                        {availableCollections.map(coll => (
                          <button
                            key={coll.id}
                            type="button"
                            onClick={() => handleCollectionSelect(coll)}
                            className={`px-3 py-2 rounded-md text-sm transition-colors ${
                              selectedCollection?.id === coll.id
                                ? 'bg-amber-600 text-white'
                                : 'bg-white border hover:bg-neutral-100'
                            }`}
                          >
                            {coll.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Material (if 4-tier) */}
                  {selectedCollection && availableMaterials.length > 0 && (
                    <div>
                      <Label className="text-sm text-neutral-600">3. Select Material</Label>
                      <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto">
                        {availableMaterials.map(mat => (
                          <button
                            key={mat.id}
                            type="button"
                            onClick={() => handleMaterialSelect(mat)}
                            className={`px-3 py-2 rounded-md text-sm transition-colors ${
                              selectedMaterial?.id === mat.id
                                ? 'bg-amber-600 text-white'
                                : 'bg-white border hover:bg-neutral-100'
                            }`}
                          >
                            {mat.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 4: Color */}
                  {availableColors.length > 0 && (
                    <div>
                      <Label className="text-sm text-neutral-600">
                        {availableMaterials.length > 0 ? '4' : '3'}. Select Color
                      </Label>
                      <div className="grid grid-cols-4 gap-2 mt-1 max-h-40 overflow-y-auto">
                        {availableColors.map(color => (
                          <button
                            key={color.id}
                            type="button"
                            onClick={() => handleColorSelect(color)}
                            className={`flex flex-col items-center p-2 rounded-md text-xs transition-colors ${
                              selectedColor?.id === color.id
                                ? 'bg-amber-100 border-2 border-amber-600'
                                : 'bg-white border hover:bg-neutral-50'
                            }`}
                          >
                            <div 
                              className="w-8 h-8 rounded-md border mb-1"
                              style={{ backgroundColor: color.hex_color || '#ccc' }}
                            />
                            <span className="truncate w-full text-center">{color.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Selection Summary */}
                  {selectedColor && (
                    <div className="bg-white p-3 rounded-md border border-amber-200">
                      <p className="text-sm font-medium text-amber-800">Selected:</p>
                      <p className="text-sm text-neutral-600 flex items-center gap-1 mt-1">
                        {selectedBrand?.name}
                        <ChevronRight className="w-3 h-3" />
                        {selectedCollection?.name}
                        {selectedMaterial && (
                          <>
                            <ChevronRight className="w-3 h-3" />
                            {selectedMaterial.name}
                          </>
                        )}
                        <ChevronRight className="w-3 h-3" />
                        <span className="flex items-center gap-1">
                          <span 
                            className="w-4 h-4 rounded border inline-block"
                            style={{ backgroundColor: selectedColor.hex_color || '#ccc' }}
                          />
                          {selectedColor.name}
                        </span>
                      </p>
                    </div>
                  )}
                </div>

                {/* Title */}
                <div>
                  <Label>Title</Label>
                  <Input
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                    placeholder="Auto-generated from selection"
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <Label>Image</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                    className="mt-1"
                  />
                  <p className="text-xs text-neutral-500 mt-1">
                    Recommended: 1920x1200px, 16:10 aspect ratio
                  </p>
                </div>

                <Button type="submit" className="w-full" disabled={uploading || !selectedColor}>
                  {uploading ? 'Uploading...' : 'Upload Visualization'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <Label className="text-sm">Product Type:</Label>
          <select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className="border rounded-md p-2 ml-2"
            data-testid="viz-product-filter"
          >
            {productTypes.map(pt => (
              <option key={pt.id} value={pt.id}>{pt.name}</option>
            ))}
          </select>
        </div>
        <div className="relative min-w-[220px] flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input value={vizSearch} onChange={(e) => setVizSearch(e.target.value)} placeholder="Search title, color, article code, material…" className="pl-9 h-9" data-testid="viz-search" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={relinking} onClick={handleRelink} data-testid="viz-relink-btn" title="Match visualizations to the current catalog colors and stamp article codes">
            {relinking ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link2 className="w-4 h-4 mr-1" />}
            Re-link colors
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={syncing}
            onClick={async () => {
              try {
                setSyncing(true);
                const res = await axios.post(`${API_URL}/admin/fabric-collections/sync-to-gallery`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                const d = res.data;
                toast.success(`Sync complete: ${d.created_in_gallery} new, ${d.updated_in_gallery} updated, ${d.viz_copied_to_collection} viz synced`);
                fetchVisualizations();
              } catch (err) {
                toast.error('Sync failed');
              } finally { setSyncing(false); }
            }}
            data-testid="viz-sync-gallery-btn"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Sync Gallery
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            All ({filteredVisualizations.length})
          </Button>
          <Button
            variant={viewMode === 'missing' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('missing')}
          >
            Missing ({colorsWithoutViz.length})
          </Button>
          <Button
            variant={viewMode === 'unlinked' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('unlinked')}
            className={viewMode === 'unlinked' ? '' : unlinkedCount ? 'border-orange-300 text-orange-700' : ''}
            data-testid="viz-view-unlinked"
          >
            Unlinked ({unlinkedCount})
          </Button>
        </div>
      </div>

      {/* View Mode: Missing Colors → generation queue */}
      {viewMode === 'missing' && (
        <MissingRoomsPanel token={token} productType={productFilter} roomTypes={roomTypes} styleOptions={styleOptions} onGenerated={fetchVisualizations} />
      )}

      {/* View Mode: Grid of existing visualizations (all / unlinked) */}
      {viewMode === 'unlinked' && unlinkedCount > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm text-orange-800 space-y-3" data-testid="viz-unlinked-note">
          <p><Unlink className="w-4 h-4 inline mr-2" />These AI rooms point to colors that no longer exist in the catalog (renamed or replaced during a catalog sync). Select them to link in bulk to a real catalog color, or delete them.</p>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={() => setBulkSelected(new Set(unlinkedIds))} data-testid="bulk-select-all">Select all ({unlinkedIds.length})</Button>
            {bulkSelected.size > 0 && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setBulkSelected(new Set())}>Clear ({bulkSelected.size})</Button>}
            <div className="ml-auto flex gap-2">
              <Button size="sm" className="h-7 text-xs bg-sky-600 hover:bg-sky-700" disabled={!bulkSelected.size || bulkBusy} onClick={() => setShowBulkLinkDialog(true)} data-testid="bulk-link-btn"><Link2 className="w-3 h-3 mr-1" /> Link {bulkSelected.size || ''} to a color</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-700 bg-white hover:bg-red-50" disabled={!bulkSelected.size || bulkBusy} onClick={() => handleBulkDelete(false)} data-testid="bulk-delete-selected-btn"><Trash2 className="w-3 h-3 mr-1" /> Delete selected</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-700 bg-white hover:bg-red-50" disabled={bulkBusy} onClick={() => handleBulkDelete(true)} data-testid="bulk-delete-all-btn"><Trash2 className="w-3 h-3 mr-1" /> Delete all unlinked</Button>
            </div>
          </div>
        </div>
      )}
      <Dialog open={showBulkLinkDialog} onOpenChange={setShowBulkLinkDialog}>
        <DialogContent className="max-w-lg" data-testid="bulk-link-dialog">
          <DialogHeader><DialogTitle>Link {bulkSelected.size} visualization(s) to a catalog color</DialogTitle></DialogHeader>
          <VizColorLinker allColors={allColors} value={bulkLink} onChange={(c) => setBulkLink({ brand_name: c.brand_name, collection_name: c.collection_name, material_name: c.material_name, color_id: c.id, color_name: c.name, color_code: c.code })} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkLinkDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkLink} disabled={!bulkLink?.color_id || bulkBusy} className="bg-sky-600 hover:bg-sky-700" data-testid="bulk-link-confirm">{bulkBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link2 className="w-4 h-4 mr-1" />} Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {(viewMode === 'grid' || viewMode === 'unlinked') && (
        <VizGallery
          all={resolvedVisualizations.filter(v => viewMode !== 'unlinked' || !v.linked)}
          items={filteredVisualizations.filter(v => viewMode !== 'unlinked' || !v.linked)}
          filters={{ brand_id: brandFilter, collection_id: collectionFilter, material_id: materialFilter }}
          setFilters={(f) => { setBrandFilter(f.brand_id); setCollectionFilter(f.collection_id); setMaterialFilter(f.material_id); }}
          fabricCollections={fabricCollections} viewMode={viewMode} bulkSelected={bulkSelected} toggleBulk={toggleBulk}
          compareSelection={compareSelection} toggleCompare={toggleCompareSelection}
          onPreview={handlePreview} onEdit={handleEdit} onDownload={handleDownload} onDelete={handleDelete} loading={loading}
        />
      )}

      <SharePreviewDialog open={shareOpen} onOpenChange={setShareOpen} token={token} items={compareSelection} onEmail={(init) => { setCompareSelection([]); setShareComposer(init); }} />
      <SharedLinksDialog open={sharedLinksOpen} onOpenChange={setSharedLinksOpen} token={token} onEmail={setShareComposer} />
      <EmailComposer open={!!shareComposer} onOpenChange={(o) => !o && setShareComposer(null)} token={token} initial={shareComposer} title="Send room previews" agentName={user?.name || user?.email || brand?.brand_name || ''}
        onSend={async (payload) => { await axios.post(`${API_URL}/admin/emails/compose`, { ...payload, reference_type: shareComposer?.reference_type, reference_id: shareComposer?.reference_id }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(`Sent to ${payload.to.join(', ')}`); }}
        onSchedule={async (payload, sendAt) => { await axios.post(`${API_URL}/admin/emails/schedule`, { kind: 'email', send_at: sendAt, payload: { ...payload, reference_type: shareComposer?.reference_type, reference_id: shareComposer?.reference_id } }, { headers: { Authorization: `Bearer ${token}` } }); }} />

      {/* AI Generation Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-600" />
              Generate AI Visualization
            </DialogTitle>
          </DialogHeader>
          {selectedColorForAI && (
            <div className="space-y-4">
              <div className="bg-neutral-50 p-3 rounded-lg">
                <p className="text-sm font-medium">Generating for:</p>
                <div className="flex items-center gap-2 mt-2">
                  <div 
                    className="w-10 h-10 rounded border"
                    style={{ backgroundColor: selectedColorForAI.hex_color || '#ccc' }}
                  />
                  <div>
                    <p className="font-medium text-sm">{selectedColorForAI.name}</p>
                    <p className="text-xs text-neutral-500">{selectedColorForAI.path}</p>
                  </div>
                </div>
              </div>
              
              <div>
                <Label>Room Type</Label>
                <select
                  value={aiForm.room_type}
                  onChange={(e) => setAiForm({ ...aiForm, room_type: e.target.value })}
                  className="w-full border rounded-md p-2 mt-1"
                >
                  {roomTypes.map(room => (
                    <option key={room} value={room}>{room.charAt(0).toUpperCase() + room.slice(1)}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Style</Label>
                <select
                  value={aiForm.style}
                  onChange={(e) => setAiForm({ ...aiForm, style: e.target.value })}
                  className="w-full border rounded-md p-2 mt-1"
                >
                  {stylePresets.map(style => (
                    <option key={style} value={style}>{style.charAt(0).toUpperCase() + style.slice(1)}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Additional Description (optional)</Label>
                <Input
                  value={aiForm.description}
                  onChange={(e) => setAiForm({ ...aiForm, description: e.target.value })}
                  placeholder="e.g., large windows, natural light"
                />
              </div>
              
              <div>
                <Label>Number of Images</Label>
                <select
                  value={aiForm.count}
                  onChange={(e) => setAiForm({ ...aiForm, count: parseInt(e.target.value) })}
                  className="w-full border rounded-md p-2 mt-1"
                >
                  <option value={1}>1 image</option>
                  <option value={2}>2 images</option>
                  <option value={3}>3 images</option>
                </select>
              </div>
              
              <Button 
                onClick={handleGenerateAI} 
                disabled={generating}
                className="w-full bg-amber-600 hover:bg-amber-700"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Generate Visualization</>
                )}
              </Button>
              
              {/* Batch Room Styles Option */}
              <div className="text-center">
                <button
                  onClick={() => {
                    setShowAIDialog(false);
                    setShowBatchRoomDialog(true);
                  }}
                  className="text-sm text-purple-600 hover:text-purple-700 underline"
                >
                  Or generate for multiple room types at once →
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Visualization</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                placeholder="Visualization title"
              />
            </div>
            <div>
              <Label>Product Type</Label>
              <select
                value={editForm.product_type}
                onChange={(e) => setEditForm({ ...editForm, product_type: e.target.value })}
                className="w-full border rounded-md p-2 mt-1"
              >
                {productTypes.map(pt => (
                  <option key={pt.id} value={pt.id}>{pt.name}</option>
                ))}
              </select>
            </div>
            <VizColorLinker
              allColors={allColors}
              value={editLink}
              onChange={(c) => setEditLink({ brand_id: c.brand_id, brand_name: c.brand_name, collection_id: c.collection_id, collection_name: c.collection_name, material_id: c.material_id, material_name: c.material_name, color_id: c.id, color_name: c.name, color_code: c.code, color_hex: c.hex_color })}
            />
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="viz-edit-save">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog with AI Regeneration */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedViz?.title || 'Preview'}</DialogTitle>
          </DialogHeader>
          {selectedViz && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Image Preview - Takes 2 columns on large screens */}
              <div className="lg:col-span-2 space-y-4">
                <div className="relative bg-neutral-100 rounded-lg overflow-hidden">
                  <ImageWithLoading 
                    src={vizThumb(selectedViz, 1024)}
                    alt={selectedViz.title}
                    className="w-full h-auto max-h-[50vh] object-contain"
                    colorHex={selectedViz.color_hex}
                  />
                </div>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex flex-wrap gap-2">
                    {selectedViz.color_code && (
                      <span className="text-xs font-mono bg-emerald-50 text-emerald-700 px-2 py-1 rounded" data-testid="viz-preview-code">{selectedViz.color_code}</span>
                    )}
                    <span className="text-xs bg-neutral-100 px-2 py-1 rounded">{selectedViz.product_type}</span>
                    {selectedViz.fabric_material && (
                      <span className="text-xs bg-neutral-100 px-2 py-1 rounded">{selectedViz.fabric_material}</span>
                    )}
                    {selectedViz.fabric_color && (
                      <span className="text-xs bg-neutral-100 px-2 py-1 rounded flex items-center gap-1">
                        {selectedViz.color_hex && (
                          <span className="w-3 h-3 rounded border" style={{ backgroundColor: selectedViz.color_hex }} />
                        )}
                        {selectedViz.fabric_color}
                      </span>
                    )}
                    {selectedViz.ai_generated && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> AI Generated
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleOpenOriginal(selectedViz)}>
                      <ExternalLink className="w-4 h-4 mr-1" /> Open
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDownload(selectedViz)}>
                      <Download className="w-4 h-4 mr-1" /> Download
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        // Generate shareable link
                        const vizUrl = selectedViz.external_url || `${process.env.REACT_APP_BACKEND_URL}${selectedViz.image_url}`;
                        const shareUrl = `${window.location.origin}/visualizations/${selectedViz.id}`;
                        
                        // Copy to clipboard
                        navigator.clipboard.writeText(vizUrl).then(() => {
                          toast.success('Image URL copied to clipboard!');
                        }).catch(() => {
                          // Fallback for older browsers
                          const input = document.createElement('input');
                          input.value = vizUrl;
                          document.body.appendChild(input);
                          input.select();
                          document.execCommand('copy');
                          document.body.removeChild(input);
                          toast.success('Image URL copied to clipboard!');
                        });
                      }}
                    >
                      <Link className="w-4 h-4 mr-1" /> Share
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* AI Room Image Generator Panel */}
              <div className="lg:col-span-1 border-t lg:border-t-0 lg:border-l border-neutral-200 pt-4 lg:pt-0 lg:pl-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-neutral-900">AI Room Generator</h4>
                </div>
                <p className="text-xs text-neutral-500 mb-4">
                  Generate new visualization variations with different room settings
                </p>
                
                <div className="space-y-4">
                  {/* Room Type */}
                  <div>
                    <Label className="text-xs font-medium text-neutral-600 mb-1 block">Room Type</Label>
                    <select
                      value={regenForm.room_type}
                      onChange={(e) => setRegenForm({ ...regenForm, room_type: e.target.value })}
                      className="w-full text-sm border rounded-md px-3 py-2 bg-white"
                    >
                      <option value="living room">Living Room</option>
                      <option value="bedroom">Bedroom</option>
                      <option value="dining room">Dining Room</option>
                      <option value="home office">Home Office</option>
                      <option value="kitchen">Kitchen</option>
                      <option value="nursery">Nursery</option>
                      <option value="bathroom">Bathroom</option>
                      <option value="sunroom">Sunroom</option>
                      <option value="media room">Media Room</option>
                    </select>
                  </div>
                  
                  {/* Interior Style */}
                  <div>
                    <Label className="text-xs font-medium text-neutral-600 mb-1 block">Interior Style</Label>
                    <select
                      value={regenForm.style}
                      onChange={(e) => setRegenForm({ ...regenForm, style: e.target.value })}
                      className="w-full text-sm border rounded-md px-3 py-2 bg-white"
                    >
                      <option value="modern minimalist">Modern Minimalist</option>
                      <option value="scandinavian">Scandinavian</option>
                      <option value="contemporary">Contemporary</option>
                      <option value="mid-century modern">Mid-Century Modern</option>
                      <option value="coastal">Coastal</option>
                      <option value="industrial">Industrial</option>
                      <option value="bohemian">Bohemian</option>
                      <option value="traditional">Traditional</option>
                      <option value="farmhouse">Farmhouse</option>
                      <option value="art deco">Art Deco</option>
                      <option value="japandi">Japandi</option>
                      <option value="transitional">Transitional</option>
                    </select>
                  </div>
                  
                  {/* Interior Color Scheme */}
                  <div>
                    <Label className="text-xs font-medium text-neutral-600 mb-1 block">Interior Color Scheme</Label>
                    <select
                      value={regenForm.interior_color_scheme}
                      onChange={(e) => setRegenForm({ ...regenForm, interior_color_scheme: e.target.value })}
                      className="w-full text-sm border rounded-md px-3 py-2 bg-white"
                    >
                      <option value="neutral">Neutral (whites, grays, beiges)</option>
                      <option value="warm">Warm (terracotta, ochre, rust)</option>
                      <option value="cool">Cool (blues, greens, grays)</option>
                      <option value="earthy">Earthy (browns, greens, tans)</option>
                      <option value="monochromatic">Monochromatic</option>
                      <option value="bold and colorful">Bold & Colorful</option>
                      <option value="pastel">Pastel</option>
                      <option value="dark and moody">Dark & Moody</option>
                      <option value="light and airy">Light & Airy</option>
                    </select>
                  </div>
                  
                  {/* Additional Description */}
                  <div>
                    <Label className="text-xs font-medium text-neutral-600 mb-1 block">Additional Details (optional)</Label>
                    <textarea
                      value={regenForm.description}
                      onChange={(e) => setRegenForm({ ...regenForm, description: e.target.value })}
                      placeholder="E.g., Large bay window, morning sunlight, plants..."
                      className="w-full text-sm border rounded-md px-3 py-2 h-16 resize-none"
                    />
                  </div>
                  
                  {/* Number of Variations */}
                  <div>
                    <Label className="text-xs font-medium text-neutral-600 mb-1 block">Generate Variations</Label>
                    <div className="flex gap-2">
                      {[1, 2, 3].map((num) => (
                        <button
                          key={num}
                          onClick={() => setRegenForm({ ...regenForm, count: num })}
                          className={`flex-1 py-2 text-sm rounded-md border transition-colors ${
                            regenForm.count === num
                              ? 'border-purple-600 bg-purple-50 text-purple-700'
                              : 'border-neutral-200 hover:border-neutral-300'
                          }`}
                        >
                          {num} image{num > 1 ? 's' : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Generate Button */}
                  <Button
                    onClick={handleRegenerateVariations}
                    disabled={regenerating}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {regenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating {regenForm.count} variation{regenForm.count > 1 ? 's' : ''}...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate {regenForm.count} New Variation{regenForm.count > 1 ? 's' : ''}
                      </>
                    )}
                  </Button>
                  
                  {/* Save as Favorite Button */}
                  <button
                    onClick={() => saveFavoriteRoomStyle({
                      room_type: regenForm.room_type,
                      style: regenForm.style,
                      interior_color_scheme: regenForm.interior_color_scheme,
                      description: regenForm.description
                    })}
                    className="w-full text-sm text-neutral-500 hover:text-purple-600 flex items-center justify-center gap-1 py-2 transition-colors"
                  >
                    <Heart className="w-4 h-4" /> Save style to favorites
                  </button>
                  
                  {/* Favorite Room Styles */}
                  {favoriteRoomStyles.length > 0 && (
                    <div className="border-t pt-4 mt-2">
                      <Label className="text-xs font-medium text-neutral-600 mb-2 block">Saved Favorites</Label>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {favoriteRoomStyles.map(fav => (
                          <div 
                            key={fav.id} 
                            className="flex items-center justify-between p-2 bg-purple-50 rounded text-xs group"
                          >
                            <button
                              onClick={() => applyFavoriteStyle(fav)}
                              className="flex-1 text-left text-purple-700 hover:text-purple-900"
                            >
                              {fav.room_type} · {fav.style}
                            </button>
                            <button
                              onClick={() => removeFavoriteRoomStyle(fav.id)}
                              className="text-neutral-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-neutral-400 text-center">
                    New images will appear in the gallery after generation
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Compare Dialog - Side by Side Comparison */}
      <Dialog open={showCompareDialog} onOpenChange={setShowCompareDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Compare Visualizations ({compareSelection.length})
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {compareSelection.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-neutral-500">Select visualizations from the grid to compare</p>
              </div>
            ) : (
              <div className={`grid gap-4 ${
                compareSelection.length === 1 ? 'grid-cols-1' :
                compareSelection.length === 2 ? 'grid-cols-2' :
                compareSelection.length === 3 ? 'grid-cols-3' : 'grid-cols-2 lg:grid-cols-4'
              }`}>
                {compareSelection.map(viz => (
                  <div key={viz.id} className="border rounded-lg overflow-hidden bg-white">
                    <div className="relative aspect-[3/4] bg-neutral-100">
                      <ImageWithLoading 
                        src={vizThumb(viz, 768)}
                        alt={viz.title}
                        className="w-full h-full object-cover"
                        colorHex={viz.color_hex}
                      />
                      <button
                        onClick={() => toggleCompareSelection(viz)}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="p-3">
                      <p className="font-medium text-sm truncate">{viz.title}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-neutral-500">
                        {viz.color_hex && (
                          <span className="w-4 h-4 rounded border" style={{ backgroundColor: viz.color_hex }} />
                        )}
                        <span>{viz.fabric_color}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 flex-1">
              <Button 
                variant="outline" 
                onClick={async () => {
                  // Download comparison as combined image
                  try {
                    toast.loading('Creating comparison image...');
                    // Create a canvas to combine images
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    
                    const imgWidth = 400;
                    const imgHeight = 533;  // 3:4 aspect ratio
                    const padding = 20;
                    const cols = compareSelection.length <= 2 ? compareSelection.length : 2;
                    const rows = Math.ceil(compareSelection.length / cols);
                    
                    canvas.width = cols * imgWidth + (cols + 1) * padding;
                    canvas.height = rows * (imgHeight + 60) + (rows + 1) * padding;  // +60 for title
                    
                    // Fill background
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    
                    // Load and draw each image
                    for (let i = 0; i < compareSelection.length; i++) {
                      const viz = compareSelection[i];
                      const col = i % cols;
                      const row = Math.floor(i / cols);
                      const x = padding + col * (imgWidth + padding);
                      const y = padding + row * (imgHeight + 60 + padding);
                      
                      // Draw title background
                      ctx.fillStyle = '#f5f5f5';
                      ctx.fillRect(x, y + imgHeight, imgWidth, 50);
                      
                      // Draw title text
                      ctx.fillStyle = '#333333';
                      ctx.font = 'bold 14px system-ui';
                      ctx.fillText(viz.title || 'Visualization', x + 10, y + imgHeight + 25);
                      ctx.font = '12px system-ui';
                      ctx.fillStyle = '#666666';
                      ctx.fillText(viz.fabric_color || '', x + 10, y + imgHeight + 42);
                      
                      // Load and draw image
                      try {
                        const imgSrc = viz.external_url || `${process.env.REACT_APP_BACKEND_URL}${viz.image_url}`;
                        const img = new window.Image();
                        img.crossOrigin = 'anonymous';
                        await new Promise((resolve, reject) => {
                          img.onload = resolve;
                          img.onerror = reject;
                          img.src = imgSrc;
                        });
                        ctx.drawImage(img, x, y, imgWidth, imgHeight);
                      } catch (imgErr) {
                        // Draw placeholder if image fails
                        ctx.fillStyle = viz.color_hex || '#cccccc';
                        ctx.fillRect(x, y, imgWidth, imgHeight);
                        ctx.fillStyle = '#666666';
                        ctx.font = '16px system-ui';
                        ctx.fillText('Image unavailable', x + imgWidth/2 - 60, y + imgHeight/2);
                      }
                    }
                    
                    // Download
                    const link = document.createElement('a');
                    link.download = `comparison-${Date.now()}.png`;
                    link.href = canvas.toDataURL('image/png');
                    link.click();
                    toast.dismiss();
                    toast.success('Comparison image downloaded');
                  } catch (error) {
                    toast.dismiss();
                    toast.error('Failed to create comparison image');
                    console.error(error);
                  }
                }}
                className="flex items-center gap-2"
                disabled={compareSelection.length === 0}
              >
                <Download className="w-4 h-4" />
                Download
              </Button>
              <Button variant="outline" onClick={() => setCompareSelection([])}>
                Clear
              </Button>
            </div>
            <Button variant="outline" onClick={() => setShowCompareDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Batch Room Styles Dialog */}
      <Dialog open={showBatchRoomDialog} onOpenChange={setShowBatchRoomDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Grid className="w-5 h-5" />
              Batch Generate Room Styles
            </DialogTitle>
          </DialogHeader>
          
          {selectedColorForAI && (
            <div className="py-4 space-y-4">
              <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
                <div 
                  className="w-12 h-12 rounded-lg border"
                  style={{ backgroundColor: selectedColorForAI.hex_color || '#ccc' }}
                />
                <div>
                  <p className="font-medium">{selectedColorForAI.name}</p>
                  <p className="text-xs text-neutral-500">{selectedColorForAI.path}</p>
                </div>
              </div>
              
              {/* Room Types Multi-Select */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Select Room Types</Label>
                <div className="grid grid-cols-3 gap-2">
                  {['living room', 'bedroom', 'dining room', 'home office', 'kitchen', 'nursery', 'bathroom', 'sunroom', 'media room'].map(room => (
                    <button
                      key={room}
                      onClick={() => toggleBatchRoomType(room)}
                      className={`p-2 text-xs rounded-md border transition-colors ${
                        batchRoomForm.room_types.includes(room)
                          ? 'bg-amber-100 border-amber-300 text-amber-800'
                          : 'bg-white border-neutral-200 hover:border-amber-200'
                      }`}
                    >
                      {room.charAt(0).toUpperCase() + room.slice(1)}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-neutral-400 mt-1">{batchRoomForm.room_types.length} room(s) selected</p>
              </div>
              
              {/* Style */}
              <div>
                <Label className="text-sm font-medium mb-1 block">Interior Style</Label>
                <select
                  value={batchRoomForm.style}
                  onChange={(e) => setBatchRoomForm({ ...batchRoomForm, style: e.target.value })}
                  className="w-full text-sm border rounded-md px-3 py-2"
                >
                  <option value="modern minimalist">Modern Minimalist</option>
                  <option value="scandinavian">Scandinavian</option>
                  <option value="contemporary">Contemporary</option>
                  <option value="mid-century modern">Mid-Century Modern</option>
                  <option value="coastal">Coastal</option>
                  <option value="industrial">Industrial</option>
                  <option value="bohemian">Bohemian</option>
                  <option value="traditional">Traditional</option>
                  <option value="farmhouse">Farmhouse</option>
                </select>
              </div>
              
              {/* Color Scheme */}
              <div>
                <Label className="text-sm font-medium mb-1 block">Interior Color Scheme</Label>
                <select
                  value={batchRoomForm.interior_color_scheme}
                  onChange={(e) => setBatchRoomForm({ ...batchRoomForm, interior_color_scheme: e.target.value })}
                  className="w-full text-sm border rounded-md px-3 py-2"
                >
                  <option value="neutral">Neutral (whites, grays, beiges)</option>
                  <option value="warm">Warm (terracotta, ochre, rust)</option>
                  <option value="cool">Cool (blues, greens, grays)</option>
                  <option value="earthy">Earthy (browns, greens, tans)</option>
                  <option value="monochromatic">Monochromatic</option>
                </select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBatchRoomDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBatchRoomGenerate}
              disabled={generating || batchRoomForm.room_types.length === 0}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Generate {batchRoomForm.room_types.length} Room{batchRoomForm.room_types.length > 1 ? 's' : ''}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Style Presets Management Dialog */}
      <Dialog open={showPresetsDialog} onOpenChange={setShowPresetsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Grid className="w-5 h-5" />
              Style Presets
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-neutral-500 mb-4">
              Manage predefined style configurations for consistent AI-generated visualizations.
            </p>
            
            {/* Existing Presets */}
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {stylePresets.map(preset => (
                <div 
                  key={preset.id} 
                  className="border rounded-lg p-4 hover:bg-neutral-50 transition-colors"
                >
                  {editingPreset === preset.id ? (
                    // Edit mode
                    <div className="space-y-3">
                      <Input
                        value={preset.name}
                        onChange={(e) => updateStylePreset(preset.id, { name: e.target.value })}
                        placeholder="Preset name"
                        className="font-medium"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <select
                          value={preset.room_type}
                          onChange={(e) => updateStylePreset(preset.id, { room_type: e.target.value })}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="living room">Living Room</option>
                          <option value="bedroom">Bedroom</option>
                          <option value="dining room">Dining Room</option>
                          <option value="home office">Home Office</option>
                          <option value="kitchen">Kitchen</option>
                        </select>
                        <select
                          value={preset.style}
                          onChange={(e) => updateStylePreset(preset.id, { style: e.target.value })}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="modern minimalist">Modern Minimalist</option>
                          <option value="scandinavian">Scandinavian</option>
                          <option value="contemporary">Contemporary</option>
                          <option value="coastal">Coastal</option>
                          <option value="industrial">Industrial</option>
                          <option value="bohemian">Bohemian</option>
                          <option value="traditional">Traditional</option>
                        </select>
                        <select
                          value={preset.interior_color_scheme}
                          onChange={(e) => updateStylePreset(preset.id, { interior_color_scheme: e.target.value })}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="neutral">Neutral</option>
                          <option value="warm">Warm</option>
                          <option value="cool">Cool</option>
                          <option value="earthy">Earthy</option>
                        </select>
                      </div>
                      <Input
                        value={preset.description || ''}
                        onChange={(e) => updateStylePreset(preset.id, { description: e.target.value })}
                        placeholder="Description (optional)"
                        className="text-sm"
                      />
                      <Button size="sm" onClick={() => setEditingPreset(null)}>
                        Done
                      </Button>
                    </div>
                  ) : (
                    // View mode
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-neutral-900">{preset.name}</h4>
                        <p className="text-xs text-neutral-500 mt-1">
                          {preset.room_type} · {preset.style} · {preset.interior_color_scheme}
                        </p>
                        {preset.description && (
                          <p className="text-xs text-neutral-400 mt-1">{preset.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => applyStylePreset(preset)}
                          className="text-amber-600 hover:text-amber-700"
                        >
                          Apply
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => setEditingPreset(preset.id)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-500 hover:text-red-600"
                          onClick={() => deleteStylePreset(preset.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {stylePresets.length === 0 && (
                <p className="text-center text-neutral-400 py-8">No style presets yet. Create one below.</p>
              )}
            </div>
            
            {/* Add New Preset */}
            <div className="border-t mt-4 pt-4">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  saveStylePreset({
                    name: `New Preset ${stylePresets.length + 1}`,
                    room_type: 'living room',
                    style: 'modern minimalist',
                    interior_color_scheme: 'neutral',
                    description: ''
                  });
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Preset
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPresetsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
